import React from 'react';
import { isNil } from 'lodash';

import { claimBarCellMaxWidth } from '~/components/ClaimPage/ClaimPageBar/ClaimPageBarUtils';
import type { ResolvedClaimBarEntry } from '~/components/ClaimPage/hooks/useFetchResolvedClaimBar';
import { ContactEntity } from '~/components/Contact';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { lossLocationToString } from '~/components/GlobalLossLocation';
import {
  configuredFieldValueText,
  TEXT_VALUE_FIELD_TYPES,
} from '~/components/IncidentConfiguration/configuredFieldValueText';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import type { HierarchyHints } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { ResolvedClaimFieldsResponseFieldType as FieldType } from '~/server_shared/openapi/data-contracts';

import GenericClaimBarField from './GenericClaimBarField';

// The full value re-renders the cell's own dark text, so the tooltip is white rather than the default
// translucent grey, and a long value scrolls inside it.
const VALUE_TOOLTIP_CLASS =
  'bg-white text-slate-900 text-sm p-8 border border-solid border-slate-200 shadow-md max-h-[200px] overflow-y-auto';

type HierarchicalValue = { parent: string | null; child: string | null };

// A code the hints do not label is left out rather than shown raw, as the built-in Incident Type cell and
// the address formatter do. A parent with no coded children stores its child as free text.
const hierarchicalLabel = (value: HierarchicalValue, hierarchy?: Pick<HierarchyHints, 'values'>): string | null => {
  const parentOption = hierarchy?.values?.find((option) => option.value === value.parent);
  if (!parentOption) return null;
  const childLabel = parentOption.children.length
    ? parentOption.children.find((option) => option.value === value.child)?.label
    : value.child;
  return [parentOption.label, childLabel].filter(Boolean).join(' / ');
};

/** The two catalog types that are not configured field types, so the shared value formatter does not know them. */
const renderersByFieldType: Partial<Record<FieldType, (entry: ResolvedClaimBarEntry) => React.ReactNode>> = {
  // The same local date and time format TimeOfLoss shows.
  [FieldType.Datetime]: ({ value }) => serverDateTimeToLocal(value),
  [FieldType.Hierarchical]: ({ value, hints }) => hierarchicalLabel(value, hints?.hierarchy),
};

// `enum_values` are the options an administrator can choose today; `value_labels` name every value this claim
// holds, a retired option's included, so they win.
const getOptionLabels = ({ enum_values, value_labels }: ResolvedClaimBarEntry): Record<string, string> => ({
  ...Object.fromEntries((enum_values ?? []).map(({ value, label }) => [value, label])),
  ...value_labels,
});

type FormatCurrency = (amount: number) => string;

// A held value no source names is left out by the formatter rather than rendered as an empty entry.
const renderValueText = (entry: ResolvedClaimBarEntry, formatCurrency: FormatCurrency): string | null => {
  const options = Object.entries(getOptionLabels(entry)).map(([id, desc]) => ({ id, desc }));
  return configuredFieldValueText(entry.field_type as string, entry.value, { options, formatCurrency }) || null;
};

const getValueRenderer = (fieldType: string | null | undefined) => {
  if (!fieldType) return null;
  const renderer = renderersByFieldType[fieldType as FieldType];
  if (renderer) return (entry: ResolvedClaimBarEntry) => (isNil(entry.value) ? null : renderer(entry));
  if (TEXT_VALUE_FIELD_TYPES.includes(fieldType)) return renderValueText;
  if (fieldType === FieldType.Location) {
    return ({ value }: ResolvedClaimBarEntry) => (isNil(value) ? null : lossLocationToString(value) || null);
  }
  return null;
};

type ConfiguredClaimBarFieldProps = {
  entry: ResolvedClaimBarEntry;
};

/** A catalog field's cell, rendered from the payload alone - its path is never read. */
const ConfiguredClaimBarField = ({ entry }: ConfiguredClaimBarFieldProps) => {
  const { currencyFormatter } = useCurrencyFormatter();

  // A contact keeps its own one-line cell: the person icon that opens its card, and a name that truncates with
  // its own tooltip. Each contact gets its own ContactEntity, so a name read for an earlier one never shows here.
  if (entry.field_type === FieldType.Contact) {
    const content = entry.value ? (
      <ContactEntity key={entry.value} contactId={entry.value} maxWidth={claimBarCellMaxWidth} />
    ) : null;
    return <GenericClaimBarField title={entry.display_name} allowWrapping={false} content={content} />;
  }

  const renderValue = getValueRenderer(entry.field_type);
  if (!renderValue) return null;

  const value = renderValue(entry, currencyFormatter.format);
  const content = isNil(value) ? null : (
    <OverflowTextWithToolTip
      maxWidth={claimBarCellMaxWidth}
      tooltipMaxWidth={360}
      tooltipClassName={VALUE_TOOLTIP_CLASS}
    >
      {value}
    </OverflowTextWithToolTip>
  );

  return <GenericClaimBarField title={entry.display_name} allowWrapping={false} content={content} />;
};

export default ConfiguredClaimBarField;
